import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

/** Navnet prosessoren er registrert med i worklet-modulen */
const prosessornavn = 'lydnivaa-maaler';

/**
 * Worklet-modulen kopieres rett under webapp-rota av "assets" i angular.json – i en underkatalog ville SPA-omskrivingen
 * i StatiskeWebRessurser (lederplan-tjenester) servert index.html i stedet. Må serveres fra samme opphav som appen,
 * da Content-Security-Policy (script-src) ikke tillater blob:-urler.
 */
function workletUrl(): string {
    return new URL('lydnivaa-maaler.worklet.js', document.baseURI).href;
}

/**
 * Måler lydnivået i en lydstrøm vhja en AudioWorklet, se src/worklets/lydnivaa-maaler.worklet.js.
 *
 * Workleten kjører på lydtråden, og leverer dermed målinger jevnt også når fanen ligger i bakgrunnen – i motsetning
 * til requestAnimationFrame, som stanser helt, og timere, som strupes. Måleren tar ingen beslutninger selv,
 * den melder bare hver måling videre.
 *
 * En måler brukes for én lydstrøm: {@link start} én gang, og {@link stopp} når den ikke trengs lenger.
 */
export class Lydnivåmåler {
    private lydkontekst?: AudioContext;
    private kilde?: MediaStreamAudioSourceNode;
    private måler?: AudioWorkletNode;
    private stoppet = false;

    /** @param måleintervallMs hvor ofte (ms) workleten melder lydnivået */
    constructor(private readonly måleintervallMs: number) {
    }

    /**
     * Starter målingen. `vedMåling` kalles med lydnivået (RMS fra 0 til 1) omtrent hvert måleintervall.
     * Feiler dersom worklet-modulen ikke kan lastes.
     */
    async start(lydstrøm: MediaStream, vedMåling: (rms: number) => void): Promise<void> {
        const lydkontekst = this.lydkontekst = new AudioContext();
        //en kontekst som ikke opprettes rett etter en brukerhandling kan starte i 'suspended', og gir da ingen målinger
        if (lydkontekst.state === 'suspended') void lydkontekst.resume();

        await lydkontekst.audioWorklet.addModule(workletUrl());
        //måleren ble stoppet mens modulen ble lastet
        if (this.stoppet) return;

        this.kilde = lydkontekst.createMediaStreamSource(lydstrøm);
        //uten utganger prosesseres noden av lydgrafen selv om den ikke er koblet til høyttaleren
        this.måler = new AudioWorkletNode(lydkontekst, prosessornavn, {
            numberOfInputs: 1,
            numberOfOutputs: 0,
            processorOptions: {måleintervallMs: this.måleintervallMs}
        });
        this.måler.port.onmessage = (melding: MessageEvent<number>) => vedMåling(melding.data);
        this.kilde.connect(this.måler);
    }

    /** Stopper målingen og frigir lydkonteksten. Kan kalles flere ganger, også før {@link start} er ferdig */
    stopp(): void {
        this.stoppet = true;

        if (erIkkeNil(this.måler)) {
            this.måler.port.onmessage = null;
            this.måler.port.close();
            this.måler.disconnect();
            this.måler = undefined;
        }

        this.kilde?.disconnect();
        this.kilde = undefined;

        void this.lydkontekst?.close();
        this.lydkontekst = undefined;
    }
}
